import { useCallback } from "react";
import { debugLog } from "../common/utils/debug";
import { connectionManager } from "../services/session-connection-manager.service";
import { createUserMessage } from "../common/utils/message.utils";
import { avatarHealthMonitor } from "../services/avatar-health-monitor.service";
import { setManualSendInProgress } from "../services/session-connection-event-handlers";

interface UseMessageHandlingProps {
  addMessage: (message: any) => void;
  setMuted?: (muted: boolean) => void;
  clearTranscription?: () => void;
}

// Module-level timing state for performance tracking
let messageStartTime: number | null = null;

export const getMessageStartTime = () => messageStartTime;
export const setMessageStartTime = (time: number | null) => {
  messageStartTime = time;
};

/**
 * Hook for handling message sending logic
 */
export const useMessageHandling = ({
  addMessage,
  setMuted,
  clearTranscription,
}: UseMessageHandlingProps) => {
  avatarHealthMonitor.updateActivity();
  const handleSendMessage = useCallback(
    (text: string) => {
      debugLog("🚀 [ManualSend] handleSendMessage called with text:", text);
      debugLog("🚀 [ManualSend] Timestamp:", Date.now());

      if (!connectionManager.isConnected()) {
        debugLog("❌ [ManualSend] Not connected, aborting");
        return;
      }

      // Set flag to prevent duplicate messages from audio events
      debugLog("🔒 [ManualSend] Setting manualSendInProgress = TRUE");
      setManualSendInProgress(true);

      // Mute mic and clear transcription to stop audio processing
      if (setMuted) {
        debugLog("🔇 [ManualSend] Calling setMuted(true)");
        setMuted(true);
      }
      if (clearTranscription) {
        debugLog("🧹 [ManualSend] Calling clearTranscription()");
        clearTranscription();
      }

      // Track message send time for performance monitoring
      messageStartTime = performance.now();

      // Send to backend - backend will echo back via user_message_text event
      debugLog("📤 [ManualSend] Sending text to backend via sendTextInput");
      connectionManager.sendTextInput(text);

      // Fallback: reset flag after 5s in case backend event never arrives
      setTimeout(() => {
        debugLog("⏰ [ManualSend] 5s fallback timeout - resetting manualSendInProgress = FALSE");
        setManualSendInProgress(false);
      }, 5000);
    },
    [addMessage, setMuted, clearTranscription]
  );

  return {
    handleSendMessage,
  };
};
